// @givmo/org-console — the read-level half of the ONE standing read (shell contract v3).
//
// ══ TWO PROJECTIONS, ONE SHAPE ═════════════════════════════════════════════════════════════════
//
// A host learns what its viewer may do from exactly one read — the org self read for a host on the
// `/org` family, and an older class-specific self read for a host that still carries its per-module
// standing as an open bag. Contract v3 puts the SAME four additive fields on both projections, so a
// host normalizes its read here and every rule downstream (the squares, the landing, the sidebar,
// the preferences page) is written against this one shape:
//
//   `permissions`   — the caller's EFFECTIVE permission set: the base role's strings ∪ every held
//                     module's granted-role catalog ∪ the person's custom set. The ONE authority the
//                     web consults for a row or a door (the build law: permission checks, never
//                     admin checks).
//   `capabilities`  — the class-INDEPENDENT capability names the effective set satisfies, resolved
//                     server-side. An org page, the Admin Console tile and the Turn-on door declare a
//                     CAPABILITY, so the web never learns a class-specific string for them.
//   `pendingAccessRequests` — how many requests wait on the caller across every module they may
//                     approve (a Global Admin: all; a module Owner: theirs). Absent ⇒ 0.
//   `defaultLandingModule`  — the caller's chosen landing module, when set. The landing rule's
//                     first input, riding this read so the root awaits nothing it does not await
//                     today.
//   `comingSoonModules` — OM-33: the modules announced to the organization's class, live, that THIS
//                     organization is not offered. Only the square derivation reads it (the popup's
//                     "Coming soon" tile); to everything else the module stays absent.
//
// ⚠ `null` MEANS NOT REPORTED, and it is the permissive posture the tile rule already takes. A read
// that carried no `permissions` list at all (a backend predating the field, a read still in flight
// through a stale cache) hands `null` down, and every consumer treats that as "every declared row is
// a plain link" — the mirror of "every built tile is an ordinary link". An EMPTY list is a different
// fact: the read answered and this person holds nothing, which greys every gated row.

/**
 * The three capabilities an Admin Console page declares — one per page, class-independent.
 *
 * ⚠ THE WIRE SPELLS A CAPABILITY AS THE ENUM'S VALUE (snake_case) — `write_settings`, not the
 * member name `WRITE_SETTINGS` (PR-BE1 wire note, `c4ea77664`: `ORG_CAPABILITY_VALUES`). The
 * readers below compare case-insensitively so a host handing either spelling is read the same.
 */
export const CAPABILITY_WRITE_SETTINGS = 'write_settings';
export const CAPABILITY_WRITE_MEMBER_ACCESS = 'write_member_access';
export const CAPABILITY_READ_EVENTS = 'read_events';
/**
 * The org-account deactivation act's capability (Org Machinery OM-9) — the ONE thing the
 * *Deactivate organization* control's presence rides. The server puts it on the standing read only
 * while the act is switched on for the environment AND the caller's effective set carries the
 * class's deactivation string, so the control appears on a settled positive and never on a tier.
 */
export const CAPABILITY_DEACTIVATE_ORG = 'deactivate_org';
/**
 * Saving the organization's ONE default QR design and rendering its public QR code (FCM-WEB4, plan
 * §FC-14). One class-mapped capability: which permission string satisfies it is the backend's data per
 * class, and the server puts it on the standing read only while the organization QR design is switched
 * on for the environment. The editor's Save and the Profile page's QR card ride it — never a string.
 */
export const CAPABILITY_MANAGE_ORG_QR = 'manage_org_qr';
/**
 * Taking YOURSELF off the organization's team — "Leave organization" (EP-MNT9 (c), the org removal
 * op's self-target carve-out). Who may leave is each class's own data on the backend, the same rule
 * its own removal door keeps; the *Leave* card rides this capability — never a tier.
 */
export const CAPABILITY_LEAVE_ORG = 'leave_org';

/**
 * The Admin Console tile renders iff the SETTLED effective set carries one of these — never on an
 * `isGlobalAdmin` flag (the build law). Each `/org` page declares its own; the tile shows when any
 * is held, and a Global Admin holds all three by tier.
 */
export const ADMIN_CONSOLE_CAPABILITIES: readonly string[] = [
  CAPABILITY_WRITE_SETTINGS,
  CAPABILITY_WRITE_MEMBER_ACCESS,
  CAPABILITY_READ_EVENTS,
];

/** The read-level half of one standing read, normalized away from either projection's wire. */
export interface StandingRead {
  /** The caller's effective permission set, or `null` when the read reported none. */
  permissions: readonly string[] | null;
  /** The class-independent capability names the caller holds, or `null` when not reported. */
  capabilities: readonly string[] | null;
  /** Requests waiting on the caller, across every module they may approve. `0` when not reported. */
  pendingAccessRequests: number;
  /** The caller's chosen default landing module, or `null` when unset or not reported. */
  defaultLandingModule: string | null;
  /**
   * OM-33 — the modules "coming soon" to this organization: announced to its class and live, but
   * not offered to it. Empty when the read reported none — a backend predating the field reads as
   * no announcement, never as a guess.
   */
  comingSoonModules: readonly string[];
  /**
   * The organization's time zone in force (an IANA name): the one it chose in Settings, else the one
   * its state derives. Fundraiser deadlines and campaign emails are set in it. `null` when the read
   * carried none (no choice and no state, or a backend predating the field); a page then sets times
   * in the reader's own zone and names it.
   */
  timeZone: string | null;
}

/** A read that reported nothing at all — the value every consumer treats permissively. */
export const UNREPORTED_STANDING_READ: StandingRead = {
  permissions: null,
  capabilities: null,
  pendingAccessRequests: 0,
  defaultLandingModule: null,
  comingSoonModules: [],
  timeZone: null,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A string list, or `null` when the field is absent or not a list at all. */
function stringListOrNull(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item): item is string => typeof item === 'string');
}

/**
 * Normalize the read-level fields off EITHER projection's body.
 *
 * Both projections spell them identically (`permissions`, `capabilities`,
 * `pending_access_requests`, `default_landing_module`, `coming_soon_modules`), so one reader serves
 * both hosts. Every field is read tolerantly in the direction stated in the header: a missing list
 * is NOT REPORTED, never an empty set; a missing count is zero; a missing or blank landing key is
 * unset; a missing coming-soon list is no announcement (only `/org/{org_ref}/me` carries it).
 */
export function standingReadFrom(body: unknown): StandingRead {
  if (!isRecord(body)) return UNREPORTED_STANDING_READ;
  const pending = body.pending_access_requests;
  const landing = body.default_landing_module;
  const zone = body.time_zone;
  return {
    permissions: stringListOrNull(body.permissions),
    capabilities: stringListOrNull(body.capabilities),
    pendingAccessRequests:
      typeof pending === 'number' && Number.isInteger(pending) && pending >= 0 ? pending : 0,
    defaultLandingModule:
      typeof landing === 'string' && landing.trim().length > 0 ? landing.trim() : null,
    comingSoonModules: slugList(body.coming_soon_modules),
    timeZone: typeof zone === 'string' && zone.trim().length > 0 ? zone.trim() : null,
  };
}

/** A list of module slugs: trimmed, non-empty, each once. Anything else reads as none. */
function slugList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  const slugs: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const slug = item.trim();
    if (slug.length > 0 && !slugs.includes(slug)) slugs.push(slug);
  }
  return slugs;
}

/**
 * Whether a SETTLED capability list carries any Admin Console capability.
 *
 * `null` (not reported) answers `false`: the tile "appears when the standing read settles, never
 * vanishes", so it must not be drawn on a guess and then withdrawn. This is the one place the
 * permissive posture is NOT taken, and it is the tile's own contract that decides it.
 */
export function holdsAdminConsoleCapability(capabilities: readonly string[] | null): boolean {
  if (capabilities === null) return false;
  return ADMIN_CONSOLE_CAPABILITIES.some((capability) => holdsCapability(capabilities, capability));
}

/**
 * Whether a settled capability list carries one named capability. `null` answers `false`.
 * Case-insensitive: the wire's `write_settings` and the enum member's `WRITE_SETTINGS` are one
 * capability.
 */
export function holdsCapability(
  capabilities: readonly string[] | null,
  capability: string,
): boolean {
  if (capabilities === null) return false;
  const wanted = capability.toLowerCase();
  return capabilities.some((held) => held.toLowerCase() === wanted);
}

/**
 * The per-module approval string R9 mints at every module's birth — the one string a module's
 * Team page and its request rows are gated on. Named ONCE here so the Team row, the requests panel
 * and the preferences page's approver audience cannot spell it three ways.
 */
export function approveAccessRequestsPermission(slug: string): string {
  return `${slug}:approve_access_requests`;
}

/**
 * The per-module INVITE string (D-CPO-24 clause 7): what a module's Owner holds to offer someone
 * that one app from inside it. The invite panel's module view admits a holder of it beside a
 * confirmed Global Admin, and the list read follows the same admission — the server's "same
 * authority as creating one". Named ONCE here, like the approval string.
 */
export function inviteAdminsPermission(slug: string): string {
  return `${slug}:invite_admins`;
}

// ⚠ There is deliberately NO "is this person an approver" predicate here. Who may approve is the
// server's answer, stated once there: it rides the ONE standing read as counts
// (`pending_access_requests`, per module and in total), and the server serves the approver's own
// preference definitions only to approvers. Since EP-WEB36 (D-EP-58) no per-module approval policy
// narrows that answer, so the counts follow the strings; an older backend in the deploy window still
// masked them by the retired `global_admins_only`, which a web-side predicate would have missed.
